'use client';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

// Initialize Google Analytics
export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId || measurementId === 'your-google-analytics-id-here') return;

    // Initialize gtag if not already loaded
    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        page_path: pathname,
      });
    }

    // Track page view on route change
    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    window.gtag('event', 'page_view', {
      page_path: url,
      page_title: document.title,
    });
  }, [pathname, searchParams]);
}

// Track custom events
export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

// Track conversion events
export function trackConversion(conversionType: string, value?: number, currency: string = 'USD') {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value: value,
    currency: currency,
  });
}

// Track form submissions
export function trackFormSubmit(formName: string, formData?: Record<string, any>) {
  trackEvent('form_submit', {
    form_name: formName,
    ...formData,
  });
}

// Track button clicks
export function trackButtonClick(buttonName: string, location?: string) {
  trackEvent('button_click', {
    button_name: buttonName,
    location: location || window.location.pathname,
  });
}

// Track user engagement
export function trackEngagement(engagementType: string, details?: Record<string, any>) {
  trackEvent('user_engagement', {
    engagement_type: engagementType,
    ...details,
  });
}

// Track location selection
export function trackLocationSelection(method: 'geolocation' | 'manual', location: { city: string; state: string; zipCode: string }) {
  trackEvent('location_selected', {
    selection_method: method,
    city: location.city,
    state: location.state,
    zip_code: location.zipCode,
  });
}

// Track hardware store pricing lookup
export function trackHardwareStorePricing(partName: string, location: { city: string; state: string }, storeCount: number, averagePrice: number) {
  trackEvent('hardware_store_pricing', {
    part_name: partName,
    city: location.city,
    state: location.state,
    store_count: storeCount,
    average_price: averagePrice,
  });
}

// Track manual price adjustment
export function trackPriceAdjustment(originalPrice: number, adjustedPrice: number, adjustmentType: 'percentage' | 'fixed', reason: string) {
  trackEvent('price_adjustment', {
    original_price: originalPrice,
    adjusted_price: adjustedPrice,
    adjustment_amount: adjustedPrice - originalPrice,
    adjustment_type: adjustmentType,
    adjustment_reason: reason,
  });
}

// Declare gtag types for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}